import React, { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { S, A, DARK, labelStyle } from '~/app/ui/tokens';
import { Menu } from '~/app/ui/Menu';
import { useThemePref } from '~/app/ui/theme';
import { tr, isZh } from '~/app/ui/lang';
import { NavGroup, initials } from './navModel';

type User = { displayName?: string; username?: string; isSuperAdmin?: boolean };

type Props = {
  groups: NavGroup[];
  pathname: string;
  user: User;
  roleLabel: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onLogout: () => void;
  /** In the mobile drawer: full labels, no collapse button. */
  drawer?: boolean;
  onNavigate?: () => void;
};

// The Faraday Future mark, from the asset CDN.
const FF_LOGO = 'https://genesis-cdn.ff.com/ff.svg';

const useStyles = createUseStyles({
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: 240,
    background: S.chrome,
    borderRight: `1px solid ${S.line}`,
    fontFamily: S.ui,
    color: S.fg,
    transition: 'width 160ms ease'
  },
  collapsed: { width: 64 },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: 56,
    padding: '0 12px 0 16px',
    flexShrink: 0
  },
  brandCollapsed: { padding: 0, justifyContent: 'center' },
  mark: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 28,
    flexShrink: 0,
    borderRadius: 4,
    textDecoration: 'none',
    '&:focus-visible': A.focus,
    '& img': { display: 'block', height: 22, width: 'auto' },
    // The logo is drawn near-black; light on the dark sidebar.
    [DARK]: { '& img': { filter: 'invert(1)' } }
  },
  wordmark: { fontSize: 17, fontWeight: 600, letterSpacing: '-0.005em', flex: 1, color: S.fg, textDecoration: 'none' },
  iconButton: {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: 8,
    color: S.muted,
    cursor: 'pointer',
    '& .material-icons': { fontSize: 20 },
    '&:hover': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': A.focus
  },
  nav: { flex: 1, overflowY: 'auto', padding: '4px 10px 12px' },
  group: { marginTop: 14, '&:first-child': { marginTop: 4 } },
  groupLabel: { ...labelStyle, fontSize: 11, padding: '0 10px 6px', whiteSpace: 'nowrap' },
  groupRule: { height: 1, margin: '0 12px 10px', background: S.line },
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 36,
    padding: '0 10px',
    marginBottom: 2,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    color: '#3F4248',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '& .material-icons': { fontSize: 20, color: S.muted, flexShrink: 0 },
    '&:hover': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': A.focus,
    [DARK]: { color: S.fg }
  },
  itemCollapsed: { justifyContent: 'center', padding: 0, width: 44, margin: '0 auto 2px' },
  active: {
    background: A.surface,
    color: S.fg,
    fontWeight: 600,
    boxShadow: `0 0 0 1px ${S.line}, 0 1px 2px rgba(23, 26, 32, 0.06)`,
    '& .material-icons': { color: S.fg },
    '&:hover': { background: A.surface }
  },
  label: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
  badge: {
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 10,
    background: S.fg,
    color: S.onFg,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '20px',
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums'
  },
  badgeDot: {
    position: 'absolute',
    top: 6,
    right: 8,
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: S.amber,
    boxShadow: `0 0 0 2px ${S.chrome}`
  },
  foot: { flexShrink: 0, padding: 10, borderTop: `1px solid ${S.line}` },
  aiGuide: { display: 'block', padding: '0 8px 8px', fontSize: 11, color: S.muted, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
  account: {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: 6,
    borderRadius: 10,
    cursor: 'pointer',
    '&:hover, &[aria-expanded="true"]': { background: S.chromeHover },
    '&:focus-visible': A.focus
  },
  accountCollapsed: { justifyContent: 'center', padding: '6px 0' },
  avatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: '50%',
    background: '#D8DCE1',
    color: S.fg,
    fontFamily: S.label,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.02em',
    [DARK]: { background: S.fieldLine }
  },
  who: { display: 'grid', minWidth: 0, flex: 1, lineHeight: 1.25 },
  name: { fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  role: { fontSize: 12, color: S.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chev: { fontSize: '18px !important', color: S.muted },
  menuWho: { display: 'grid', gap: 2, '& b': { fontSize: 14 }, '& span': { fontSize: 12, color: S.muted } }
});

/**
 * Genesis's navigation: grouped by job, collapsible to icons. The account
 * menu at the bottom holds the things you rarely need (language, logging out).
 */
const Sidebar: React.FC<Props> = ({ groups, pathname, user, roleLabel, collapsed, onToggleCollapsed, onLogout, drawer, onNavigate }) => {
  const [themePref, setThemePref] = useThemePref();
  const c = useStyles();
  const accountBtn = useRef<HTMLButtonElement>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const small = collapsed && !drawer;
  const name = user.displayName || user.username || 'Signed in';
  // Back to this screen after switching (a path on this site, not a full address).
  const nextUrl = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '';

  return (
    <aside className={[c.root, small ? c.collapsed : ''].join(' ')} style={drawer ? { width: 280 } : undefined} data-testid="sidebar" data-collapsed={small || undefined}>
      <div className={[c.brand, small ? c.brandCollapsed : ''].join(' ')}>
        {small ? (
          <button type="button" className={c.iconButton} onClick={onToggleCollapsed} aria-label="Expand navigation" title="Expand navigation" data-testid="nav-expand">
            <i className="material-icons" aria-hidden>last_page</i>
          </button>
        ) : (
          <>
            <NavLink to="/admin" className={c.mark} aria-label="Genesis home" onClick={onNavigate}>
              <img src={FF_LOGO} alt="" />
            </NavLink>
            <NavLink to="/admin" className={c.wordmark} onClick={onNavigate} tabIndex={-1}>Faraday Future</NavLink>
            {!drawer && (
              <button type="button" className={c.iconButton} onClick={onToggleCollapsed} aria-label="Collapse navigation" title="Collapse navigation" data-testid="nav-collapse">
                <i className="material-icons" aria-hidden>first_page</i>
              </button>
            )}
          </>
        )}
      </div>

      <nav className={c.nav} aria-label="Main">
        {groups.map(g => (
          <div key={g.id} className={c.group} data-nav-group={g.id}>
            {small ? <div className={c.groupRule} aria-hidden /> : <div className={c.groupLabel}>{g.label}</div>}
            {g.items.map(item => {
              const active = item.match.test(pathname);
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  className={[c.item, small ? c.itemCollapsed : '', active ? c.active : ''].join(' ')}
                  isActive={() => active}
                  aria-current={active ? 'page' : undefined}
                  title={small ? (item.badgeHint ? `${item.label} · ${item.badgeHint}` : item.label) : item.badgeHint}
                  aria-label={item.badgeHint ? `${item.label}, ${item.badgeHint}` : undefined}
                  data-nav={item.id}
                  onClick={onNavigate}
                >
                  <i className="material-icons" aria-hidden>{item.icon}</i>
                  {!small && <span className={c.label}>{item.label}</span>}
                  {item.badge ? (small ? <span className={c.badgeDot} aria-hidden /> : <span className={c.badge} data-nav-badge>{item.badge}</span>) : null}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className={c.foot}>
        {/* For AI assistants that use the browser: where Genesis explains how to work in it. */}
        {!small && (
          <a href="/llms.txt" className={c.aiGuide} data-testid="sidebar-ai-guide">AI assistants: read /llms.txt</a>
        )}
        <button
          ref={accountBtn}
          type="button"
          className={[c.account, small ? c.accountCollapsed : ''].join(' ')}
          aria-haspopup="menu"
          aria-expanded={accountOpen}
          aria-label={`Account: ${name}`}
          title={small ? name : undefined}
          data-testid="account-button"
          onClick={() => setAccountOpen(o => !o)}
        >
          <span className={c.avatar} aria-hidden>{initials(name)}</span>
          {!small && (
            <>
              <span className={c.who}>
                <span className={c.name}>{name}</span>
                <span className={c.role}>{roleLabel}</span>
              </span>
              <i className={`material-icons ${c.chev}`} aria-hidden>unfold_more</i>
            </>
          )}
        </button>
        {accountOpen && accountBtn.current && (
          <Menu
            anchor={accountBtn.current}
            above
            align="start"
            minWidth={232}
            label="Account"
            testId="account-menu"
            header={(
              <div className={c.menuWho}>
                <b>{name}</b>
                <span>{user.username && user.username !== name ? `${user.username} · ${roleLabel}` : roleLabel}</span>
              </div>
            )}
            groups={[
              // Light, dark, or the computer's setting; kept in this browser.
              [
                { key: 'theme-light', label: tr('Light', '浅色'), icon: 'light_mode', checked: themePref === 'light', onSelect: () => setThemePref('light') },
                { key: 'theme-dark', label: tr('Dark', '深色'), icon: 'dark_mode', checked: themePref === 'dark', onSelect: () => setThemePref('dark') },
                { key: 'theme-system', label: tr('Match system', '跟随系统'), icon: 'brightness_auto', checked: themePref === 'system', onSelect: () => setThemePref('system') }
              ],
              [isZh()
                ? { key: 'lang', label: 'English', icon: 'translate', hint: '切换到英文', href: `/setlanguage/en?next=${nextUrl}` }
                : { key: 'lang', label: '简体中文', icon: 'translate', hint: 'Switch to Chinese', href: `/setlanguage/zh?next=${nextUrl}` }],
              [{ key: 'logout', label: tr('Log out', '退出账号'), icon: 'logout', onSelect: onLogout }]
            ]}
            onClose={(returnFocus) => { setAccountOpen(false); if (returnFocus) accountBtn.current?.focus(); }}
          />
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
